import { useState, FormEvent } from 'react';
import { Button } from './ui/button';
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Reveal } from './Reveal';

const roleOptions = [
  'Provider / ACO',
  'Health System',
  'Payer',
  'Digital Health',
  'Other',
];

export function CallToAction() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    const form = event.currentTarget;

    try {
      const formData = new FormData(form);
      formData.append('access_key', 'f4bcc742-d673-4927-939e-7698449d1237');

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        form.reset();
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
        setErrorMessage(result.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Failed to send message. Please check your connection and try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(to bottom right, #34AFFB, #70D6C5)' }}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Content */}
          <Reveal as="div" direction="right" className="text-white space-y-6">
            <div>
              <h2 className="text-4xl mb-4">
                Book a demo.
              </h2>
              <div className="w-12 h-1 bg-white/40 mb-6" />
              <p className="text-lg text-white/90">
                If you're a risk-bearing organization including providers, ACOs, or payers, see how you can indentify care gaps early enough to improve outcomes and reduce risk.
              </p>
              <p className="text-lg text-white/90 mt-4">
                With a demo, we'll show you how to:
              </p>
            </div>
            <div className="space-y-4 text-white/80">
              <p className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                Idenitfy care gaps before they become missed opportunities
              </p>
              <p className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                Give clinicians actionable insights without disrupting their workflow
              </p>
              <p className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                Improve quality performance and financial outcomes
              </p>
            </div>
          </Reveal>

          {/* Right Form */}
          <Reveal as="div" direction="left" delay={120} className="bg-white rounded-2xl p-8 shadow-2xl">
            <h3 className="text-xl font-semibold text-[#1f2937] mb-6">Let's build something that works.</h3>

            {submitStatus === 'success' && (
              <div
                className="flex items-center gap-2 p-4 mb-6 rounded-md"
                style={{ backgroundColor: '#15803d', color: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              >
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm font-medium">Thank you! We'll get back to you soon.</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div
                className="flex items-center gap-2 p-4 mb-6 rounded-md"
                style={{ backgroundColor: '#b91c1c', color: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
              >
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm font-medium">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm mb-2 text-[#1f2937]">
                  Name <span className="text-red-500">*</span>
                </label>
                <Input id="name" name="name" type="text" placeholder="Jane Doe" className="w-full" required />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm mb-2 text-[#1f2937]">
                  Work Email <span className="text-red-500">*</span>
                </label>
                <Input id="email" name="email" type="email" placeholder="jane@organization.com" className="w-full" required />
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm mb-2 text-[#1f2937]">
                  Organization <span className="text-red-500">*</span>
                </label>
                <Input id="organization" name="organization" type="text" placeholder="Your organization" className="w-full" required />
              </div>

              <div>
                <label htmlFor="role" className="block text-sm mb-2 text-[#1f2937]">
                  Role / Organization Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  required
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-[#1f2937]"
                >
                  <option value="">Select one…</option>
                  {roleOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm mb-2 text-[#1f2937]">
                  What are you trying to build or improve? <span className="text-red-500">*</span>
                </label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about the data you're sitting on and the action you wish you could take…"
                  rows={5}
                  className="w-full resize-none"
                  required
                />
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-[#34AFFB] hover:bg-[#34AFFB]/90 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  'Send'
                )}
              </Button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
